import React from 'react';
import { SeoHero } from '@/components/ui/seo-hero';
import { Badge } from '@/components/ui/badge';
import { ClientLogosSlider } from '@/components/ui/ClientLogosSlider';
import SeoProcessSection from '@/components/SeoProcessSection';
import BlogArticlesTestimonialsSection from '@/components/BlogArticlesTestimonialsSection';
import SeoGettingStartedSection from '@/components/SeoGettingStartedSection';
import SeoFaqSection from '@/components/SeoFaqSection';
import FinalCTA from '@/components/FinalCTA';
import Navigation from '@/components/Navigation';
import FooterWithNewsletter from '@/components/ui/footer-column';
import SEO from '@/components/SEO';
import { useSEO } from '@/hooks/useSEO';
import { getSEOConfigForPage } from '@/utils/seoUtils';
import { Shield, BookOpen, Image, Edit, UserPlus, Calendar, Copy } from 'lucide-react';
import seoHeroWorkspace from '@/assets/seo-hero-workspace.jpg';

const GenerateBlogArticlesPage = () => {
  const { fallbackMetadata } = getSEOConfigForPage('/generate-blog-articles-with-ai');
  const { seoData, canonicalUrl, noindex, nofollow } = useSEO({ fallbackMetadata });

  const blogSteps = [
    {
      icon: UserPlus,
      title: "Set up your Brand Vault",
      description: "Define your tone, audience, and rules once, and apply to every blog draft automatically. Create multiple vaults per project.",
      time: "2 minutes",
      gradient: "bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30",
      border: "border-blue-200/50 dark:border-blue-800/50",
      numberBg: "bg-blue-100 dark:bg-blue-900/50",
      numberBorder: "border-blue-300/50 dark:border-blue-700/50",
      numberText: "text-blue-700 dark:text-blue-300",
      timeBg: "bg-blue-600 dark:bg-blue-500",
      timeText: "text-white"
    },
    {
      icon: Calendar,
      title: "Plan your calendar",
      description: "Use Studio to ideate topics, plan calendars, create content, generate visuals, and edit final drafts that are ready to publish.",
      time: "Varies",
      gradient: "bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30",
      border: "border-emerald-200/50 dark:border-emerald-800/50",
      numberBg: "bg-emerald-100 dark:bg-emerald-900/50",
      numberBorder: "border-emerald-300/50 dark:border-emerald-700/50",
      numberText: "text-emerald-700 dark:text-emerald-300",
      timeBg: "bg-emerald-600 dark:bg-emerald-500",
      timeText: "text-white"
    },
    {
      icon: Copy,
      title: "Copy content to your blog",
      description: "Edit results in the same window and export to your current CMS. Create blog articles in batches, effortlessly.",
      time: "2 minutes",
      gradient: "bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/30",
      border: "border-orange-200/50 dark:border-orange-800/50",
      numberBg: "bg-orange-100 dark:bg-orange-900/50",
      numberBorder: "border-orange-300/50 dark:border-orange-700/50",
      numberText: "text-orange-700 dark:text-orange-300",
      timeBg: "bg-orange-600 dark:bg-orange-500",
      timeText: "text-white"
    }
  ];

  const blogFaqs = [
    {
      question: "Can I create full blogs with gimmefy.ai?",
      answer: "Yes. Studio generates structured drafts with intros, subheads, and CTAs—ready for refinement and publishing."
    },
    {
      question: "Will the blogs sound like my brand?",
      answer: "Always. Brand Vault ensures every draft reflects your voice, style, and guidelines."
    },
    {
      question: "Can gimmefy help plan an ongoing blog calendar?",
      answer: "Yes. The Blog Calendar Agent suggests seasonal topics and campaign-aligned hooks so your blog pipeline stays full."
    },
    {
      question: "Can I publish blogs directly from gimmefy?",
      answer: "You can export content to your CMS or publishing tools. Native publishing integrations are available for common platforms."
    },
    {
      question: "Does gimmefy support SEO-friendly blogs?",
      answer: "Yes. Studio generates keyword-rich outlines, headers, and meta descriptions to boost search visibility. It also suggests high converting longtail and shorttail keywords."
    },
    {
      question: "Is multilingual blogging supported?",
      answer: "Yes. gimmefy supports multiple languages, letting you localise content at scale. Gimmefy transcreates content into 40+ global languages."
    },
    {
      question: "Can I repurpose blogs into other formats?",
      answer: "Yes. Turn blog drafts into LinkedIn posts, carousels, or email newsletters in a few clicks."
    },
    {
      question: "Is my data secure?",
      answer: "Yes. Enterprise-grade security and Brand Vault privacy keep your drafts, data, and brand assets protected. gimmefy also refrains from using your data to train its models."
    }
  ];

  return (
    <>
      {seoData && (
        <SEO 
          data={seoData} 
          canonicalUrl={canonicalUrl}
          noindex={noindex}
          nofollow={nofollow}
        />
      )}
      
      <div className="min-h-screen bg-background">
        <Navigation />
        
        <SeoHero
          badge={
            <Badge className="bg-primary/10 text-primary border-primary/20 text-lg px-6 py-3 rounded-full font-medium shadow-lg backdrop-blur-sm">
              🚀 AI-Powered SEO Blogs That Improve Ranking
            </Badge>
          }
          mainTitle="Generate Professional Blogs with AI"
          title="Create SEO articles for weeks in one day"
          subtitle="From idea to publish-ready in minutes. Specialist-level polish, consistent tone, and a content pipeline that never runs dry—powered by gimmefy."
          primaryCta={{
            label: "Start for FREE",
            href: "/signup/contentcal"
          }}
          image={{
            src: seoHeroWorkspace,
            alt: "Professional workspace showing laptop with AI ethics content, demonstrating modern content creation environment"
          }}
        />

        <ClientLogosSlider />
        
        <SeoProcessSection 
          title="How it works: blog-ready in four steps"
          subtitle="From ideation to publication - your complete blog workflow powered by AI"
          steps={[
            {
              icon: Shield,
              title: "Lock in your brand with Brand Vault",
              description: "Start by setting the foundation. Brand Vault auto-applies your voice, values, and audience insights to every draft—so every article sounds like you, not a robot."
            },
            {
              icon: BookOpen,
              title: "Plan content ideas with Studio",
              description: "Kick off ideation in gimmefy's Studio—just drop a few prompts and instantly generate a tailored list of relevant, on-brand content angles, trending topics, and seasonal hooks for your audiences."
            },
            {
              icon: Image,
              title: "Generate SEO-friendly visuals",
              description: "Feed in a keyword, theme, or even a rough thought. Magic Prompt expands it into outlines, titles, and content angles in seconds - ready to be built into full articles."
            },
            {
              icon: Edit,
              title: "Draft and refine in one window",
              description: "Perfect your articles in Studio: set a format, generate longtail and shorttail keywords, and optimise the final result in the Result Editor, without leaving the chat."
            }
          ]}
        />
        
        <BlogArticlesTestimonialsSection />
        
        <SeoGettingStartedSection steps={blogSteps} />
        
        <FinalCTA 
          title="Leverage gimmefy's speed, brand <br/> consistency, and creativity to create content that converts." 
          subtitle="Your next article is minutes away." 
          signupUrl="/signup/contentcal"
        />
        
        <SeoFaqSection faqs={blogFaqs} />
        
        <FooterWithNewsletter />
      </div>
    </>
  );
};

export default GenerateBlogArticlesPage;